import { authFetch } from '../utils/authFetch';
import {
  Organization,
  OrganizationCreateRequest,
  OrganizationUpdateRequest,
  OrganizationSearchRequest,
  OrganizationListResponse,
  OrganizationStatistics,
  AIAssistantRequest,
  AIAssistantResponse
} from '../types/organizations';
import { CustomerCategory } from '../types/customerCategories';

const API_BASE = (process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000') + '/api/v1';

interface GmailAuthorizationStatus {
  is_authorized: boolean;
  gmail_contact_email: string | null;
  gmail_contact_name: string | null;
  authorized_at: string | null;
  token_expires_at: string | null;
}

interface GmailAuthorizationUrl {
  authorization_url: string;
  state: string;
  organization_id: string;
}

export const organizationService = {
  // Get paginated list of organizations
  async getOrganizations(
    params: OrganizationSearchRequest = {},
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<OrganizationListResponse> {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach(v => searchParams.append(key, v));
        } else {
          searchParams.append(key, String(value));
        }
      }
    });

    const response = await authFetch(`${API_BASE}/organizations?${searchParams.toString()}`, {}, accessToken, setUserAndToken);
    if (!response.ok) {
      throw new Error('Failed to fetch organizations');
    }
    return await response.json();
  },

  async getActiveOnboardingPublicShare(
    organizationId: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<any | null> {
    const response = await authFetch(`${API_BASE}/organizations/${organizationId}/public-share`, {}, accessToken, setUserAndToken);
    if (!response.ok) {
      throw new Error('Failed to fetch public share');
    }
    const data = await response.json();
    return data.data || null;
  },

  async createOnboardingPublicShare(
    organizationId: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<any> {
    const response = await authFetch(`${API_BASE}/organizations/${organizationId}/public-share`, { method: 'POST' }, accessToken, setUserAndToken);
    if (!response.ok) {
      if (response.status === 409) {
        const err = await response.json();
        throw new Error(err.detail || 'Active public link already exists');
      }
      throw new Error('Failed to create public share');
    }
    const data = await response.json();
    return data.data;
  },

  async revokeOnboardingPublicShare(
    organizationId: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<void> {
    const response = await authFetch(`${API_BASE}/organizations/${organizationId}/public-share`, { method: 'DELETE' }, accessToken, setUserAndToken);
    if (!response.ok) {
      throw new Error('Failed to revoke public share');
    }
  },

  // Get organization by ID
  async getOrganization(
    id: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<Organization> {
    const response = await authFetch(`${API_BASE}/organizations/${id}`, {}, accessToken, setUserAndToken);
    if (!response.ok) {
      throw new Error('Failed to fetch organization');
    }
    const data = await response.json();
    return data.data;
  },

  // Create new organization
  async createOrganization(
    organization: OrganizationCreateRequest,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<Organization> {
    const response = await authFetch(`${API_BASE}/organizations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(organization),
    }, accessToken, setUserAndToken);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create organization');
    }
    const data = await response.json();
    return data.data;
  },

  // Update organization
  async updateOrganization(
    id: string,
    organization: OrganizationUpdateRequest,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<Organization> {
    const response = await authFetch(`${API_BASE}/organizations/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(organization),
    }, accessToken, setUserAndToken);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update organization');
    }
    const data = await response.json();
    return data.data;
  },

  // Delete organization (soft delete)
  async deleteOrganization(
    id: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<void> {
    const response = await authFetch(`${API_BASE}/organizations/${id}`, {
      method: 'DELETE',
    }, accessToken, setUserAndToken);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete organization');
    }
  },

  // Search organizations with advanced filters
  async searchOrganizations(
    searchParams: OrganizationSearchRequest,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<OrganizationListResponse> {
    const response = await authFetch(`${API_BASE}/organizations/search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(searchParams),
    }, accessToken, setUserAndToken);
    
    if (!response.ok) {
      throw new Error('Failed to search organizations');
    }
    return await response.json();
  },

  // Get organization statistics
  async getOrganizationStatistics(
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<OrganizationStatistics> {
    const response = await authFetch(`${API_BASE}/organizations/statistics`, {}, accessToken, setUserAndToken);
    if (!response.ok) {
      throw new Error('Failed to fetch organization statistics');
    }
    const data = await response.json();
    return data.data;
  },

  // Get available organization types
  async getOrganizationTypes(
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<string[]> {
    const response = await authFetch(`${API_BASE}/settings/types`, {}, accessToken, setUserAndToken);
    if (!response.ok) {
      throw new Error('Failed to fetch organization types');
    }
    const data = await response.json();
    return data.types || [];
  },

  // Get available subscription status options
  async getSubscriptionStatusOptions(
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<string[]> {
    const response = await authFetch(`${API_BASE}/settings/subscription-status-options`, {}, accessToken, setUserAndToken);
    if (!response.ok) {
      throw new Error('Failed to fetch subscription status options');
    }
    const data = await response.json();
    return data.options || [];
  },

  // Get available subscription cycle options
  async getSubscriptionCycleOptions(
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<string[]> {
    const response = await authFetch(`${API_BASE}/settings/subscription-cycle-options`, {}, accessToken, setUserAndToken);
    if (!response.ok) {
      throw new Error('Failed to fetch subscription cycle options');
    }
    const data = await response.json();
    return data.options || [];
  },

  // Get organization onboarding status
  async getOnboardingStatus(
    organizationId: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<any> {
    const response = await authFetch(`${API_BASE}/organizations/${organizationId}/onboarding-status`, {}, accessToken, setUserAndToken);
    if (!response.ok) {
      throw new Error('Failed to fetch onboarding status');
    }
    const data = await response.json();
    return data.data;
  },


  // Get static template messages for a provider
  async getStaticTemplateMessages(
    provider: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<any> {
    const response = await authFetch(`${API_BASE}/organizations/static-template-messages/${provider}`, {}, accessToken, setUserAndToken);
    if (!response.ok) {
      throw new Error(`Failed to fetch template messages for ${provider}`);
    }
    const data = await response.json();
    return data.data;
  },

  // ===== ORGANIZATION CUSTOMER CATEGORIES =====

  // Get all customer categories connected to an organization
  async getOrganizationCustomerCategories(
    organizationId: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<CustomerCategory[]> {
    const response = await authFetch(
      `${API_BASE}/organizations/${organizationId}/customer-categories`,
      {},
      accessToken,
      setUserAndToken
    );
    if (!response.ok) {
      throw new Error('Failed to fetch organization customer categories');
    }
    const data = await response.json();
    return data.data;
  },

  // Connect categories to an organization
  async connectCategoriesToOrganization(
    organizationId: string,
    request: { category_ids: string[] },
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<{ code: number; message: string; data: { connected_count: number } }> {
    const response = await authFetch(
      `${API_BASE}/organizations/${organizationId}/customer-categories`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      },
      accessToken,
      setUserAndToken
    );
    if (!response.ok) {
      throw new Error('Failed to connect categories to organization');
    }
    return await response.json();
  },

  // Disconnect a category from an organization
  async disconnectCategoryFromOrganization(
    organizationId: string,
    categoryId: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<void> {
    const response = await authFetch(
      `${API_BASE}/organizations/${organizationId}/customer-categories/${categoryId}`,
      {
        method: 'DELETE',
      },
      accessToken,
      setUserAndToken
    );
    if (!response.ok) {
      throw new Error('Failed to disconnect category from organization');
    }
  },

  // Disconnect an AP archetype from an organization
  async disconnectAPArchetypeFromOrganization(
    organizationId: string,
    archetypeId: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<void> {
    const response = await authFetch(
      `${API_BASE}/organizations/${organizationId}/ap-archetypes/${archetypeId}`,
      {
        method: 'DELETE',
      },
      accessToken,
      setUserAndToken
    );
    if (!response.ok) {
      throw new Error('Failed to disconnect AP archetype from organization');
    }
  },

  // Disconnect a governance overlay from an organization
  async disconnectGovernanceOverlayFromOrganization(
    organizationId: string,
    overlayId: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<void> {
    const response = await authFetch(
      `${API_BASE}/organizations/${organizationId}/governance-overlays/${overlayId}`,
      {
        method: 'DELETE',
      },
      accessToken,
      setUserAndToken
    );
    if (!response.ok) {
      throw new Error('Failed to disconnect governance overlay from organization');
    }
  },

  // ===== Governance Overlays Linking =====
  async getOrganizationGovernanceOverlays(
    organizationId: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<Array<{ id: string; name: string; description?: string }>> {
    const response = await authFetch(
      `${API_BASE}/organizations/${organizationId}/governance-overlays`,
      {},
      accessToken,
      setUserAndToken
    );
    if (!response.ok) {
      throw new Error('Failed to fetch organization governance overlays');
    }
    const data = await response.json();
    return data.data;
  },

  async connectGovernanceOverlaysToOrganization(
    organizationId: string,
    request: { governance_overlay_ids: string[] },
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<{ code: number; message: string; data: { connected_count: number } }> {
    const response = await authFetch(
      `${API_BASE}/organizations/${organizationId}/governance-overlays`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      },
      accessToken,
      setUserAndToken
    );
    if (!response.ok) {
      throw new Error('Failed to connect governance overlays to organization');
    }
    return await response.json();
  },

  // ===== AP Archetypes Linking =====
  async getOrganizationAPArchetypes(
    organizationId: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<Array<{ id: string; name: string; description?: string }>> {
    const response = await authFetch(
      `${API_BASE}/organizations/${organizationId}/ap-archetypes`,
      {},
      accessToken,
      setUserAndToken
    );
    if (!response.ok) {
      throw new Error('Failed to fetch organization AP archetypes');
    }
    const data = await response.json();
    return data.data;
  },

  async connectAPArchetypesToOrganization(
    organizationId: string,
    request: { ap_archetype_ids: string[] },
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<{ code: number; message: string; data: { connected_count: number } }> {
    const response = await authFetch(
      `${API_BASE}/organizations/${organizationId}/ap-archetypes`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request)
      },
      accessToken,
      setUserAndToken
    );
    if (!response.ok) {
      throw new Error('Failed to connect AP archetypes to organization');
    }
    return await response.json();
  },

  // ===== JOB MANAGEMENT =====

  // Get job status
  async getJobStatus(
    jobId: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<{ status: string; result?: any; error?: string }> {
    const response = await authFetch(
      `${API_BASE}/redis-jobs/${jobId}/status`,
      {},
      accessToken,
      setUserAndToken
    );
    if (!response.ok) {
      throw new Error('Failed to fetch job status');
    }
    const json = await response.json();
    const data = json?.data ?? json; // unwrap BaseAPIResponse if present
    return {
      status: data?.status,
      result: data?.result,
      error: data?.error,
    };
  },

  // Trigger customer analysis for all customers in an organization
  async analyzeCustomers(
    organizationId: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<{ job_id: string; message: string }> {
    const response = await authFetch(
      `${API_BASE}/organizations/${organizationId}/customer-analysis`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      },
      accessToken,
      setUserAndToken
    );
    if (!response.ok) {
      throw new Error('Failed to start customer analysis');
    }
    const json = await response.json();
    const data = json?.data ?? json; // unwrap BaseAPIResponse if present
    return { job_id: data?.job_id, message: data?.message || json?.message };
  },

  // Trigger customer analysis for a specific customer
  async analyzeSpecificCustomer(
    organizationId: string,
    xeroContactId: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<{ job_id: string; message: string }> {
    const response = await authFetch(
      `${API_BASE}/organizations/${organizationId}/customer-analysis/${xeroContactId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      },
      accessToken,
      setUserAndToken
    );
    if (!response.ok) {
      throw new Error('Failed to start customer analysis');
    }
    const json = await response.json();
    const data = json?.data ?? json; // unwrap BaseAPIResponse if present
    return { job_id: data?.job_id, message: data?.message || json?.message };
  },

  // Connect customer to AP archetype
  async connectCustomerToAPArchetype(
    organizationId: string,
    customerId: string,
    apArchetypeId: string,
    accessToken: string,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<any> {
    const response = await authFetch(`${API_BASE}/organizations/${organizationId}/customers/${customerId}/ap-archetypes/connect`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ap_archetype_id: apArchetypeId
      }),
    }, accessToken, setUserAndToken);

    const json = await response.json();
    if (!response.ok) {
      // Fallback 401 handling if authFetch doesn't already handle it
      if (response.status === 401) {
        setUserAndToken(null, '');
      }
      throw new Error(json.detail || 'Failed to connect customer to AP archetype');
    }

    return json.data;
  },

  // AI Assistant - Answer CC/AR questions (queues worker job)
  async answerCCARQuestion(
    organizationId: string,
    request: AIAssistantRequest,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<{ job_id: string; message: string }> {
    const response = await authFetch(
      `${API_BASE}/organizations/${organizationId}/answer-cc-ar`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      },
      accessToken,
      setUserAndToken
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to queue AI Assistant question');
    }

    const json = await response.json();
    const data = json?.data ?? json;
    return { job_id: data?.job_id, message: data?.message || json?.message };
  },

  // Gmail OAuth methods
  async getGmailAuthorizationStatus(
    organizationId: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<GmailAuthorizationStatus> {
    const response = await authFetch(
      `${API_BASE}/organizations/${organizationId}/gmail/status`,
      {},
      accessToken,
      setUserAndToken
    );
    if (!response.ok) {
      throw new Error('Failed to fetch Gmail authorization status');
    }
    const json = await response.json();
    return json.data;
  },

  async getGmailAuthorizationUrl(
    organizationId: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<GmailAuthorizationUrl> {
    const response = await authFetch(
      `${API_BASE}/organizations/${organizationId}/gmail/authorize`,
      {},
      accessToken,
      setUserAndToken
    );
    if (!response.ok) {
      throw new Error('Failed to get Gmail authorization URL');
    }
    const json = await response.json();
    return json.data;
  },

  async revokeGmailAuthorization(
    organizationId: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<void> {
    const response = await authFetch(
      `${API_BASE}/organizations/${organizationId}/gmail/revoke`,
      { method: 'DELETE' },
      accessToken,
      setUserAndToken
    );
    if (!response.ok) {
      throw new Error('Failed to revoke Gmail authorization');
    }
  },
}; 